<template>
  <div class="form-container my-2 px-3 py-2 mx-n3">
    <div
      class="row form-header"
      :class="panelOpen ? null : 'collapsed'"
      :aria-expanded="panelOpen ? 'true' : 'false'"
      :aria-controls="`form-body-${form.id}`"
      role="button"
      @click="panelOpen = !panelOpen"
    >
      <div class="col">
        {{ form.name }}
      </div>
      <div class="col">
        <b-skeleton v-if="form.last_entry === 'loading'" width="10em" />
        <span v-else>{{ form.last_entry | formatDate('M/d/yyyy h:mm a') }}</span>
      </div>
      <div class="col-auto">
        <span class="fa fa-plus form-toggle" />
      </div>
    </div>
    <b-collapse :id="`form-body-${form.id}`" v-model="panelOpen" class="mt-3 form-body">
      <template v-if="loadingSurvey || !survey">
        <b-skeleton width="100%" />
        <b-skeleton width="100%" />
        <b-skeleton width="75%" class="mb-5" />
        <b-skeleton width="70%" />
        <b-skeleton width="20%" />
        <b-skeleton width="25%" class="mb-4" />
        <b-skeleton width="68%" />
        <b-skeleton width="22%" />
        <b-skeleton width="20%" class="mb-4" />
        <b-skeleton width="100%" />
        <b-skeleton width="50%" />
        <b-skeleton width="30%" />
        <b-skeleton width="26%" class="mb-4" />
        <div class="d-flex justify-content-end">
          <b-skeleton width="4em" type="button" class="mb-2" />
        </div>
      </template>
      <Survey v-else :survey="survey" class="mb-2" />
      <div v-if="submitting" class="text-right mb-2">
        <b-spinner small /> Saving...
      </div>

      <div v-if="alert.status" class="alert alert-dismissible" :class="`alert-${alert.status}`">
        <button type="button" class="close" data-dismiss="alert" aria-label="Close">
          <span aria-hidden="true" @click="alert.status = false">&times;</span>
        </button>
        {{ alert.message }}
        <ul v-if="alert.errors.length > 0" class="error-list">
          <li v-for="error in alert.errors" :key="error">
            {{ error }}
          </li>
        </ul>
      </div>

      <template v-if="loadingSubmissions">
        <hr>
        <b-skeleton width="100%" />
      </template>
      <section v-if="submissions.length > 0" class="my-3">
        <hr>
        <div v-b-toggle="`datasource-submissions-${form.id}`" class="d-flex justify-content-between align-items-center">
          <span class="font-weight-semibold">Data Log <span class="badge badge-secondary">{{ submissions.length }}</span></span>
          <span class="fa section-toggle" />
        </div>
        <b-collapse :id="`datasource-submissions-${form.id}`" :visible="submissions.length <= 2" class="pr-2">
          <div v-for="submission in submissions" :key="submission.id">
            {{ submission.as_of | formatDate('M/d/yyyy h:mm a') }} | <a :href="getLinkToFormSubmission(submission.id)" target="_blank">View Data</a>
          </div>
          <div v-if="showMoreLink" class="text-center mt-2">
            <a :href="allDataLink" target="_blank">View more data <span class="fas fa-external-link-alt" /></a>
          </div>
        </b-collapse>
      </section>
    </b-collapse>
  </div>
</template>

<script>
import * as SurveyJS from 'survey-vue';
import '@/FormWidgets/surveyjs/surveyVueLocalization';
import * as widgets from 'surveyjs-widgets';

export default {
  name: "DataEntryItem",
  components: {
    Survey: SurveyJS.Survey
  },
  props: {
    form: {
      type: Object,
      required: true
    },
    participant: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      participantSurvey: null,
      loadingSurvey: false,
      loadingSubmissions: false,
      showMoreLink: false,
      survey: null,
      submissions: [],
      panelOpen: false,
      submitting: false,
      alert: {
        status: false,
        message: false,
        errors: []
      },
    }
  },
  computed: {
    allDataLink() {
      return `${this.$config.baseUrl}/admin/study/${this.$config.studyId}/participants/${this.participant.id}/data?source_id=${this.form.id}`;
    }
  },
  watch: {
    panelOpen() {
      // when opening this panel for the first time, load the survey and submissions
      if (this.panelOpen && !this.survey) {
        this.loadSurvey();
      }
    },
  },
  mounted() {
    this.loadSubmissions();
  },
  methods: {
    async loadSurvey() {
      try {
        this.loadingSurvey = true;
        const {ok, body} = await this.$api.Participants.getParticipantSurvey({
          'survey_id': this.form.id,
          'participant_id': this.participant.id
        });

        if (!ok) {
          throw new Error('Failed to load survey');
        }

        this.participantSurvey = body.data;
        this.initializeSurvey();
      } catch (err) {
        alert('Something went wrong');
      }
    },
    initializeSurvey(initialData = false) {
      const {survey_definition, variables} = this.participantSurvey;
      SurveyJS.Survey.cssType = "bootstrap";
      SurveyJS.defaultBootstrapCss.navigationButton = 'btn btn-dark';
      widgets.nouislider(SurveyJS);
      delete survey_definition.navigateToUrl;
      delete survey_definition.navigateToUrlOnCondition;
      this.survey = new SurveyJS.Model(survey_definition);
      if (variables) {
        Object.keys(variables).forEach((key) => {
          this.survey.setVariable(key, variables[key]);
        });
      }
      this.survey.completeText = 'Save';
      this.survey.showCompletedPage = false;

      this.survey
          .onComplete
          .add(this.submitForm);

      if (initialData) {
        this.survey.data = {...initialData}; // make copy
        // SurveyJS caches anything you set in data, which is not ideal
        // for calculated values.  Unset those so they get recalculated.
        this.survey.calculatedValues.forEach((val) => {
          this.survey.deleteDataValueCore(this.survey.valuesHash, val.propertyHash.name);
        });
      }

      this.loadingSurvey = false;
    },
    async loadSubmissions(page = 1) {
      try {
        this.loadingSubmissions = true;
        const {ok, body} = await this.$api.DataSubmissions.getDataSubmissions({
          'study_id': this.participant.study_id,
          'source_id': this.form.id,
          'participant_id': this.participant.id,
          'order_by': 'desc(as_of)',
          'page': page,
          'per_page': 50
        });

        if (!ok) {
          throw new Error('Failed to load submissions');
        }

        this.submissions = body.data;

        if (body.meta.pagination.current_page < body.meta.pagination.total_pages) {
          this.showMoreLink = true;
        }

        this.$emit('updateLastEntry', this.form.id, this.submissions[0]?.as_of ?? '');

        this.loadingSubmissions = false;
      } catch (err) {
        alert('Something went wrong');
      }
    },
    async submitForm() {
      try {
        this.submitting = true;
        // SurveyJS removes the form on complete so we have to fudge it back into existence to show
        // a read only view of it while loading
        const submittingData = JSON.parse(JSON.stringify(this.survey.data));
        this.initializeSurvey(submittingData);
        this.survey.showNavigationButtons = 'none';
        this.survey.currentPageNo = this.survey.visiblePageCount - 1; // set to last page
        this.survey.mode = 'display';

        const {ok, body} = await this.$api.DataSources.createSubmissionForDataSource({
          'source_id': this.form.id,
          'submission': {
            'participant_id': this.participant.id,
            'fields': this.survey.data
          }
        });

        if (!ok) {
          throw new Error('Failed to submit form');
        }

        // add response to beginning of array
        this.submissions.unshift(body.data);

        this.submitting = false;
        this.survey = null;


        this.setAlert({
          status: 'success',
          message: 'Data entry successfully saved.'
        });
        this.$emit('updateLastEntry', this.form.id, body.data.as_of);
        await this.loadSurvey(); // reload survey in case any variables have changed
      } catch (err) {
        alert('Something went wrong');
      }
    },
    setAlert(alert) {
      this.alert = Object.assign(this.alert, alert)
    },
    getLinkToFormSubmission(formSubmissionId) {
      return `${this.$config.baseUrl}/admin/study/${this.$config.studyId}/data/${formSubmissionId}`;
    },
  }
}
</script>

<style scoped>
.form-container {
  border: 1px solid transparent;
  border-radius: 4px;

  .form-toggle {
    cursor: pointer;
    transition: transform 0.2s ease;
  }
}

.form-container:has(> .collapse.show),
.form-container:has(> .collapsing) {
  border-color: #dee2e6;

  .form-header .col {
    font-weight: 600;
  }

  .form-toggle {
    transform: rotate(45deg);
  }
}

.sv_main {
  .sv-action-bar {
    justify-content: end;
    border: none !important;
    background: none !important;
  }
  .sv_complete_btn {
    color: #fff;
    background-color: #343a40;
    border-color: #343a40;
  }
  #sv-nav-prev {
    margin-right: 0.5rem;
  }
}
</style>
